/* eslint-disable react-refresh/only-export-components -- small helpers live beside their components */
import { useEffect, type ReactNode } from 'react'

/**
 * The signed-out frame of the console: the "ScoreLM Ops" wordmark, one
 * bordered panel and a footer line. Used by the login page and the
 * invite / forgot / reset pages.
 */
export function AuthCard({ children, footer = "League admins sign in on their league's own site." }: { children: ReactNode; footer?: ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center px-4 py-10">
      <div className="w-full max-w-sm">
        <p className="mb-6 text-center font-display text-[1.75rem] font-extrabold uppercase leading-none tracking-[0.03em] text-ink">
          Score<span className="text-accent">LM</span> Ops
        </p>
        <div className="rounded-xl border border-line bg-panel p-6 shadow-dialog sm:p-7">{children}</div>
        {footer ? <p className="mt-5 text-center text-xs text-subtle">{footer}</p> : null}
      </div>
    </div>
  )
}

/** The browsers' default policy, put back when no page set one before. */
const DEFAULT_REFERRER_POLICY = 'strict-origin-when-cross-origin'

/**
 * While mounted, the page sends no Referer: the invite page carries a
 * credential in its path. A browser applies a referrer meta when it is
 * inserted or its content changes; removing it changes nothing, so the
 * cleanup writes the previous policy (or the default) back into the meta and
 * leaves it in place, or the tab would keep no-referrer after an in-app
 * navigation away.
 */
export function useNoReferrer(): void {
  useEffect(() => {
    let meta = document.head.querySelector<HTMLMetaElement>('meta[name="referrer"]')
    const previous = meta?.content || DEFAULT_REFERRER_POLICY
    if (!meta) {
      meta = document.createElement('meta')
      meta.name = 'referrer'
      meta.content = 'no-referrer'
      document.head.appendChild(meta)
    } else {
      meta.content = 'no-referrer'
    }
    const el = meta
    return () => {
      el.content = previous
    }
  }, [])
}

/** A "text link" that is still a 44 px tap target. */
export const authLinkClass = 'inline-flex min-h-[44px] items-center text-[13px] font-semibold text-accent hover:underline'
